import { createRef, RefObject } from "react";
import isString from "lodash/isString";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { FileType } from "@/Common/Models/FileType";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { DraggableImageOverlayElement } from "@/ExportableFile/Models/OverlayElements/DraggableImageOverlayElement";
import { DraggableImageOverlayType, OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { PrepositionedSignatureOverlayElement } from "@/ExportableFile/Models/OverlayElements/PrepositionedSignatureOverlayElement";

import styles from "./FilePreviewComponentEditable.module.scss";

import img_icon_delete from "@/Globals/_images/icons/delete.png";
import img_icon_icon_checked_green from "@/Globals/_images/icons/icon-checked-green.png";
import img_icon_icon_plus_blue from "@/Globals/_images/icons/icon-plus-blue.png";
import img_icon_icons8_autograph_50 from "@/Globals/_images/icons/icons8-autograph-50.png";
import img_icon_icon_refresh from "@/Globals/_images/icons/refresh-icon.png";

interface IFilePreviewComponentEditableProps {
    file: ExportableFile;
    attachedDocuments?: ExportableFile;
    documentPages?: number[];
    dataCy?: string;
    exportMode?: ExportMode;

    onFilePreviewLoaded?: () => void;
    onFileEditLoaded?: () => void;
    onPreviewModeChange?: (mode: FilePreviewComponentMode) => void;
    changePreviewModeWhenSignatureMalicious?: () => void;

    onApplyOverlaysCallback?: (hasSignature: boolean) => void;
    allowEditMode?: boolean;
    filePreviewMode?: FilePreviewComponentMode;

    onUpdateFetchUrl?: (fetchUrl: string) => void;
    includeSignatureInPreviewMode?: boolean;
    isSignatureRemoved?: boolean;
    onOverlaysChange?: (draggableOverlays: DraggableImageOverlayElement[], prepositionedSignatureOverlays: PrepositionedSignatureOverlayElement[]) => void;
    onFileInitializeLoadingStateChange?: (isLoading: boolean, callback?: () => void) => void;
}

class FilePreviewComponentEditableState {
    public fetchUrl: string;
    public isLoaded = false;
    public onLoad: () => void;
    public mode = FilePreviewComponentMode.iFramePreview;
    public currentPage = 1;
    public actualPage = 1;
    public draggableOverlays: DraggableImageOverlayElement[] = [];
    public documentTotalPages: number;
    public userSignatureDimension: {
        width: number,
        height: number
    };
    public useSignatureImage: HTMLImageElement = new Image();
}

enum FilePreviewComponentMode {
    iFramePreview,
    imageEdit
}

class FilePreviewComponentEditable extends BaseReactComponent<IFilePreviewComponentEditableProps, FilePreviewComponentEditableState> {
    private imageContainerId = "imageContainerFilePreviewComponent";
    private imageDrawZoneId = "imageDrawZoneFilePreviewComponent";
    private onEditModePreviewLoaded: () => void;
    private isPortrait = true;
    private isOverlaySizeCalculated = false;
    private containerRef: RefObject<HTMLDivElement> = createRef();

    public floatingButtonRightPosition = 22;

    public componentDidMount(): void {
        $(window).trigger("resize");

        const defaultState = new FilePreviewComponentEditableState();
        const signatureBytes = sessionStore.getCurrentEmployee().Model.SignatureByteArray;

        if (signatureBytes) {
            defaultState.useSignatureImage.src = `data:${MyleHelper.GetImageMime(signatureBytes)};base64,${signatureBytes}`;
            defaultState.useSignatureImage.onload = () => {
                this.mutateState(s => {
                    s.userSignatureDimension = {
                        width: this.state.useSignatureImage.naturalWidth,
                        height: this.state.useSignatureImage.naturalHeight
                    };
                });
            };
        }

        this.setState(defaultState, async () => {
            const fileTypeRadOrObstetrical = [FileType.RADPDF, FileType.OBSTETRICALFILE].includes(this.props.file.fileType);

            if (fileTypeRadOrObstetrical || !this.props.includeSignatureInPreviewMode)
                this.lockFormAndInitializeFile(true, !!this.props.includeSignatureInPreviewMode);
        });
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.stopAllDrag();
    }

    public async componentDidUpdate(prevProps: IFilePreviewComponentEditableProps, prevState: FilePreviewComponentEditableState): Promise<void> {
        if (prevProps.filePreviewMode !== this.props.filePreviewMode) {
            if (this.props.allowEditMode && this.props.filePreviewMode === FilePreviewComponentMode.imageEdit && this.props.file) {
                this.switchToEditMode();
            }
        }
        else {
            const isPageNumbersDifferent =
                this.props.file.PageNumbers
                && (
                    this.props.file.PageNumbers.length !== prevProps.file.PageNumbers?.length
                    || !this.props.file.PageNumbers.every(p => prevProps.file.PageNumbers?.includes(p))
                );

            if (
                (prevProps.file != this.props.file
                    || this.props.includeSignatureInPreviewMode !== prevProps.includeSignatureInPreviewMode)
                && !this.props.isSignatureRemoved
            ) {
                const preserveOverlays =
                    prevProps.file == this.props.file
                    && prevState.mode === FilePreviewComponentMode.imageEdit
                    && this.state.mode === FilePreviewComponentMode.imageEdit;

                await this.lockFormAndInitializeFile(preserveOverlays, this.props.file.fileType === FileType.OBSTETRICALFILE ? !!this.props.includeSignatureInPreviewMode : true);

                if (isPageNumbersDifferent) {
                    this.onChangePage(1);
                }
            }
        }

        this.initAllDragInEditMode();
    }

    private async lockFormAndInitializeFile(resetOverlays: boolean = true, shouldLoadSignature: boolean = true): Promise<void> {
        if (this.props.onFileInitializeLoadingStateChange) {
            this.props.onFileInitializeLoadingStateChange(
                true,
                () => this.initializeFile(resetOverlays, shouldLoadSignature)
            );
        }
        else {
            this.initializeFile(resetOverlays, shouldLoadSignature);
        }
    }

    private async initializeFile(resetOverlays: boolean = true, shouldLoadSignature: boolean = true): Promise<void> {
        try {
            let fetchUrl = "";
            const isPreviewMode = shouldLoadSignature && this.state.mode === FilePreviewComponentMode.iFramePreview;
            const includePrepositionSignature = isPreviewMode && !!this.props.includeSignatureInPreviewMode;

            if (cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && !!this.props.attachedDocuments) {
                const file = await ExportableFileHelper.getConcatenatedFetchUrlDtoAsync(this.props.file, [this.props.attachedDocuments]);
                fetchUrl = (await file.getFetchUrlDtoAsync(includePrepositionSignature, includePrepositionSignature, isPreviewMode, false, false, this.props.exportMode))?.FetchUrl;
            }
            else {
                fetchUrl = (await this.props.file.getFetchUrlDtoAsync(includePrepositionSignature, includePrepositionSignature, isPreviewMode, false, false, this.props.exportMode))?.FetchUrl;
            }

            const isSignatureMalicious = this.copyFilesDraggableOverlays(this.props.file)?.filter(draggableOverlay => draggableOverlay.width > draggableOverlay.containerWidth);
            if (isSignatureMalicious && isSignatureMalicious.length > 0 && this.state.mode === FilePreviewComponentMode.iFramePreview) {
                fetchUrl = (await this.props.file.getFetchUrlDtoAsync(false, false, true, false, false, this.props.exportMode))?.FetchUrl;
            }

            this.mutateState(
                s => {
                    if (isSignatureMalicious && isSignatureMalicious.length > 0 && this.state.mode === FilePreviewComponentMode.iFramePreview) {
                        s.mode = FilePreviewComponentMode.imageEdit;
                        this.props.changePreviewModeWhenSignatureMalicious?.();
                        resetOverlays = true;
                    }

                    s.fetchUrl = fetchUrl;
                    if (resetOverlays)
                        s.draggableOverlays = this.copyFilesDraggableOverlays(this.props.file);

                    if (!s.documentTotalPages)
                        s.documentTotalPages = this.props.file.totalPages;
                },
                () => {
                    if (this.state.mode === FilePreviewComponentMode.imageEdit) {
                        this.updateAllOverlayElementsPositioningData(this.state.draggableOverlays);
                    }
                    this.props.onOverlaysChange?.(
                        this.state.draggableOverlays,
                        this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                    );
                }
            );

            if (!fetchUrl)
                this.props.onUpdateFetchUrl?.(fetchUrl);
        }
        catch (exception: any) {
            throw exception;
        }
        finally {
            this.props.onFileInitializeLoadingStateChange?.(false);
        }
    }

    public mutateState(mutation: (s: FilePreviewComponentEditableState) => void, then: () => void = null): void {
        const previousMode = this.state.mode;
        super.mutateState(
            mutation,
            () => {
                if (this.state.mode !== previousMode)
                    window.setTimeout(() => this.props.onPreviewModeChange?.(this.state.mode));

                then && then();
            }
        );
    }

    public render(): JSX.Element {
        if (IsNullOrWhiteSpace(this.state?.fetchUrl) || !this.props.file)
            return null;

        switch (this.state.mode) {
            case FilePreviewComponentMode.iFramePreview:
                return this.renderIFramePreviewMode();
            case FilePreviewComponentMode.imageEdit:
                return this.renderImageEditMode();
            default:
                return <div />;
        }
    }

    public renderImageEditMode(): JSX.Element {
        const drawZoneStyle: React.CSSProperties = {
            height: "100%",
            width: "100%",
            position: "absolute",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "grey",
            overflow: "hidden"
        };

        let imageContainerStyle: React.CSSProperties = {
            width: "max-content", // Container must have exactly the size width as the image to calculate overlay positions
            height: "100%",
            border: "2px black solid",
            position: "relative"
        };

        let imageStyle: React.CSSProperties = { objectFit: "contain", height: "100%" };

        // Landscape adjust
        if (!this.isPortrait) {
            imageContainerStyle = {
                width: "100%",
                height: "max-content", // Container must have exactly the size height as the image to calculate overlay positions
                border: "2px black solid",
                position: "relative"
            };
            imageStyle = { objectFit: "contain", width: "100%" };
        }

        const showOverlayButtons = this.state.isLoaded && !cacheStore.ClinicSettings.EnableQuickDocumentSigning;

        return (
            <div style={{ height: "100%" }}>
                <div style={{ height: "100%", position: "relative", zIndex: "0" }}>
                    <div
                        id={this.imageDrawZoneId}
                        className="flex-container"
                        style={drawZoneStyle}
                        data-cy={this.props.dataCy}
                    >
                        <div id={this.imageContainerId} style={imageContainerStyle} ref={this.containerRef}>
                            <img
                                id="image-jpg-printableFiles"
                                src={`${this.state.fetchUrl}&convertToJpeg=true&ConvertToJpegPageNumber=${this.state.currentPage}`}
                                style={imageStyle}
                                onLoad={() => this.onLoad()}
                            />
                            {this.state.isLoaded && this.state.draggableOverlays &&
                                this.renderOverlays()
                            }
                        </div>
                        {this.props.file.totalPages > 1 && (
                            <div style={{ position: "absolute", bottom: "5px", left: "5px" }}>
                                <SimplePagerControl
                                    PageSize={1}
                                    TotalRows={this.props.file.totalPages}
                                    CurrentPage={this.state.currentPage}
                                    changePageFunction={page => this.onChangePage(page)}
                                    isControlDisabled={!this.state.isLoaded}
                                />
                            </div>
                        )}
                    </div>
                    {showOverlayButtons &&
                        this.renderCancelButton("20px", this.floatingButtonRightPosition)
                    }
                    {showOverlayButtons &&
                        this.renderSignButton("120px", this.floatingButtonRightPosition)
                    }
                    {showOverlayButtons &&
                        this.renderApplyButton("220px", this.floatingButtonRightPosition)
                    }
                </div>
            </div>
        );
    }

    public renderIFramePreviewMode(): JSX.Element {
        return (
            <div style={{ height: "100%", position: "relative", zIndex: "0" }}>
                <img src={this.state.useSignatureImage?.src} className={styles.userSignature} />
                {this.props.file.showOverlayRefreshButton &&
                    this.renderRefreshButton("20px", this.floatingButtonRightPosition + 5 /* scroolbar */)
                }
                {this.props.allowEditMode
                    && this.state.isLoaded
                    && !cacheStore.ClinicSettings.EnableQuickDocumentSigning
                    && (this.props.file.fileType === FileType.RADPDF
                        || this.props.file.fileType === FileType.WORKERCLAIM
                        || this.props.file.fileType === FileType.OBSTETRICALFILE
                        || this.props.file.fileType === FileType.WorkSafeBcForm)
                    && this.renderSignButton("120px", this.floatingButtonRightPosition + 5 /* scroolbar */)
                }
                <iframe
                    id="iframe-pdf-printableFiles"
                    name="iframe-printableFile"
                    src={`${this.state.fetchUrl}#toolbar=0&view=FitH`}
                    height="100%"
                    width="100%"
                    onLoad={() => this.onLoadPreview()}
                    data-cy={this.props.dataCy}
                />
            </div>
        );
    }

    public renderCancelButton(marginTop: string, right: number): JSX.Element {
        return (
            <div
                className="transparentFloatingButton pointer"
                style={{ marginTop: marginTop, right: `${right}px`, padding: "11px" }}
                onClick={() => {
                    this.cancelSignatureMode();
                }}
            >
                <img src={img_icon_delete} width="25px" height="25px" style={{ paddingBottom: "1px" }} />
            </div>
        );
    }

    public renderSignButton(marginTop: string, right: number): JSX.Element {
        return (
            <div
                className="transparentFloatingButton pointer"
                style={{ marginTop: marginTop, right: `${right}px` }}
                onClick={() => {
                    this.onSignButton();
                }}
            >
                <img src={img_icon_icon_plus_blue} width="30px" height="30px" className="icon-plus-signature" />
                <img src={img_icon_icons8_autograph_50} width="40px" height="40px" style={{ position: "relative" }} />
            </div>
        );
    }

    public renderApplyButton(marginTop: string, right: number): JSX.Element {
        return (
            <div
                className="transparentFloatingButton pointer"
                style={{ marginTop: marginTop, right: `${right}px` }}
                onClick={() => this.applyOverlaysAndSwitchToPreviewMode()}
            >
                <img src={img_icon_icon_checked_green} width="40px" height="40px" />
            </div>
        );
    }

    public renderRefreshButton(marginTop: string, right: number): JSX.Element {
        return (
            <div
                className="transparentFloatingButton pointer"
                style={{ marginTop: marginTop, right: `${right}px` }}
                onClick={() => this.applyOverlaysAndSwitchToPreviewMode()}
            >
                <img src={img_icon_icon_refresh} width="40px" height="40px" />
            </div>
        );
    }

    public renderOverlays(): JSX.Element[] {
        const signatureBytes = sessionStore.getCurrentEmployee().Model.SignatureByteArray;
        const userSignatureRatio = this.state.userSignatureDimension?.width / this.state.userSignatureDimension?.height;

        return this.state.draggableOverlays
            .filter(overlay => overlay.page === this.state.actualPage)
            .map(overlay => {
                const imageContainer = $(`#${this.imageContainerId}`);
                const elementStyle: React.CSSProperties = { position: "absolute", outline: "1px grey dashed" };
                const overlayRatio = overlay.width / overlay.height;
                const isRatioDifferent = userSignatureRatio != 0 &&
                    Math.abs(userSignatureRatio - overlayRatio) > 0.4 &&
                    ((userSignatureRatio > 1 && overlayRatio < 1) || (userSignatureRatio < 1 && overlayRatio > 1));

                if (Math.abs(overlay.left) >= imageContainer.width() || Math.abs(overlay.top) >= imageContainer.height()) {
                    elementStyle.right = 0;
                    elementStyle.bottom = 0;
                }
                else {
                    if (isRatioDifferent) {
                        if (this.state.userSignatureDimension.width + overlay.left > imageContainer.width())
                            elementStyle.left = imageContainer.width() - this.state.userSignatureDimension.width;

                        if (this.state.userSignatureDimension.height + overlay.top > imageContainer.height())
                            elementStyle.top = imageContainer.height() - this.state.userSignatureDimension.height;
                    }
                    else {
                        elementStyle.left = overlay.left;
                        elementStyle.top = overlay.top;
                    }
                }

                const hasWidthInOverlay = overlay.width !== 0;
                if (hasWidthInOverlay) {
                    if (isRatioDifferent) {
                        elementStyle.width = this.state.userSignatureDimension.width;
                        elementStyle.height = this.state.userSignatureDimension.height;
                    }
                    else {
                        if (imageContainer.width() && overlay.width > imageContainer.width()) {
                            // give defualt position and width to signature
                            elementStyle.width = 200;
                            elementStyle.left = null;
                            elementStyle.top = null;
                            elementStyle.right = 0;
                            elementStyle.bottom = 0;
                        }
                        else {
                            elementStyle.width = overlay.width;
                            elementStyle.height = overlay.height;
                        }
                    }
                }
                else {
                    elementStyle.width = overlay.defaultwidth;
                }

                const hasHeight = !IsNullOrUndefined(elementStyle.height);
                return (
                    <div key={overlay.id} id={overlay.id} style={elementStyle}>
                        <div className={`dragCursor ${hasHeight ? styles.signatureImageContainer : ""}`} style={{ position: "relative" }}>
                            <div
                                className="pointer elementOverlayRemoveButton"
                                onClick={() => this.removeElement(overlay.id)}
                            >
                                <img src={img_icon_delete} height="20px" width="20px" style={{ paddingBottom: "1px" }} />
                            </div>
                            {signatureBytes && isString(signatureBytes) && (
                                <img
                                    onSelect={e => e.preventDefault}
                                    style={{ width: "100%" }}
                                    className={hasHeight ? styles.signatureImage : ""}
                                    src={`data:${MyleHelper.GetImageMime(signatureBytes)};base64,${signatureBytes}`}
                                    alt="signature"
                                    onLoad={e => {
                                        const image = e.currentTarget;
                                        if (image && !hasWidthInOverlay) {
                                            this.mutateState(s => {
                                                s.draggableOverlays = s.draggableOverlays.map(draggableOverlay => {
                                                    if (draggableOverlay.id === overlay.id) {
                                                        if (!draggableOverlay.containerWidth && !draggableOverlay.containerHeight) {
                                                            const { width, height } = this.getImageContainerWidthAndTop();
                                                            draggableOverlay.containerWidth = width;
                                                            draggableOverlay.containerHeight = height;
                                                        }
                                                        draggableOverlay.left = draggableOverlay.left ? draggableOverlay.left - image.clientWidth : 0;
                                                        draggableOverlay.top = draggableOverlay.top ? draggableOverlay.top - image.clientHeight : 0;
                                                        draggableOverlay.width = image.clientWidth;
                                                        draggableOverlay.height = image.clientHeight;
                                                    }

                                                    return draggableOverlay;
                                                });
                                            });
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                );
            });
    }

    public onLoad(): void {
        const imageInEditMode = $("#image-jpg-printableFiles");
        const imageDrawZone = $(`#${this.imageDrawZoneId}`);

        this.isPortrait = !imageInEditMode
            || !imageDrawZone
            || (imageInEditMode.height() / imageInEditMode.width() > imageDrawZone.height() / imageDrawZone.width());

        this.mutateState(
            s => s.isLoaded = true,
            () => {
                this.onEditModePreviewLoaded?.();
                this.onEditModePreviewLoaded = null;
                this.props.onFileEditLoaded?.();
            }
        );
    }

    public onLoadPreview(): void {
        this.mutateState(
            s => s.isLoaded = true,
            () => this.props.onFilePreviewLoaded && this.props.onFilePreviewLoaded()
        );
    }

    public removeElement(id: string): void {
        this.stopDrag(id);
        this.mutateState(
            s => s.draggableOverlays = s.draggableOverlays.filter(f => f.id !== id),
            () => {
                this.props.onOverlaysChange?.(
                    this.state.draggableOverlays,
                    this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                );
            }
        );
    }

    public initAllDragInEditMode(): void {
        if (this.state.mode !== FilePreviewComponentMode.imageEdit) {
            return;
        }

        let imageContainer = $(`#${this.imageContainerId}`);
        for (let filePreviewElement of this.state.draggableOverlays) {
            let elemOverlay = $(`#${filePreviewElement.id}`);

            elemOverlay.draggable({
                containment: imageContainer,
                handle: ".dragCursor",
                stop: () => this.mutateState(s => this.updateAllOverlayElementsPositioningData(s.draggableOverlays, [filePreviewElement.id])),
                start: () => {
                    // The draggable library has a bug when the botton/right styles are set instead of top/left.
                    // This hack transforms bottom/right to top/left to avoid the bug.
                    if (elemOverlay[0]?.style.left === "" && elemOverlay[0].style.top === "") {
                        elemOverlay.css("left", elemOverlay.css("left"));
                        elemOverlay.css("top", elemOverlay.css("top"));
                        elemOverlay[0].style.bottom = "";
                        elemOverlay[0].style.right = "";
                    }
                }
            });
            elemOverlay.resizable({
                containment: imageContainer,
                aspectRatio: true,
                stop: () => this.mutateState(s => this.updateAllOverlayElementsPositioningData(s.draggableOverlays, [filePreviewElement.id]))
            });
        }
    }

    public updateAllOverlayElementsData(): void {
        if (this.state.draggableOverlays?.length)
            this.updateAllOverlayElementsPositioningData(this.state.draggableOverlays);
    }

    private async updateAllOverlayElementsPositioningData(elements: DraggableImageOverlayElement[], ids?: string[]): Promise<void> {
        const container = $(`#${this.imageContainerId}`);
        let isOverlayCalculated = false;

        if (container) {
            if (ids)
                elements = elements.filter(p => ids.contains(p.id));

            for (const element of elements) {
                const elem = $(`#${element.id}`);
                const position = elem.position();

                if (position) {
                    if (this.props.file.fileType !== FileType.OBSTETRICALFILE && !this.isOverlaySizeCalculated) {
                        element.update(this.adjustOverlayToCurrentScreen(new DraggableImageOverlayElement({
                            left: position.left,
                            top: position.top,
                            containerWidth: element.containerWidth,
                            containerHeight: element.containerHeight,
                            width: elem.width(),
                            height: elem.height(),
                            page: element.page,
                            defaultwidth: element.defaultwidth,
                            id: element.id,
                            draggableImageOverlayType: element.draggableImageOverlayType,
                            overlayElementType: element.overlayElementType
                        })));

                        isOverlayCalculated = true;
                    }
                    else {
                        element.updateFromJquery(container, elem);
                    }
                }
            }

            this.isOverlaySizeCalculated = isOverlayCalculated;
        }
    }

    public stopAllDrag(): void {
        for (const filePreviewElement of this.state?.draggableOverlays)
            this.stopDrag(filePreviewElement.id);
    }

    public stopDrag(id: string): void {
        const elem = $(`#${id}`);
        elem && elem.draggable("destroy");
    }

    public cancelSignatureMode(): void {
        this.mutateState(
            s => {
                s.draggableOverlays = this.copyFilesDraggableOverlays(this.props.file);
                s.mode = FilePreviewComponentMode.iFramePreview;
            },
            () => {
                this.props.onOverlaysChange?.(
                    this.state.draggableOverlays,
                    this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                );
            }
        );
    }

    private applyDraggableOverlaysToFile(draggableOverlays: DraggableImageOverlayElement[]): void {
        this.props.file.applyDistinctOverlayElements(draggableOverlays, OverlayElementType.DraggableImage);
        this.props.onApplyOverlaysCallback?.(!!draggableOverlays.length);
    }

    public async applyOverlaysAndSwitchToPreviewMode(): Promise<void> {
        this.applyDraggableOverlaysToFile(this.state.draggableOverlays);

        for (const overlay of this.state.draggableOverlays)
            overlay.PersistDefaultSignatureSize();

        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            await this.props.file.saveOverlayPositionsIfNeeded();

        const file = this.props.attachedDocuments
            ? await ExportableFileHelper.getConcatenatedFetchUrlDtoAsync(this.props.file, [this.props.attachedDocuments])
            : this.props.file;

        const fetchUrl = (await file.getFetchUrlDtoAsync())?.FetchUrl;
        if (!IsNullOrWhiteSpace(fetchUrl))
            this.props.onUpdateFetchUrl?.(fetchUrl);

        this.mutateState(
            s => {
                s.fetchUrl = fetchUrl;
                s.isLoaded = false;
                s.mode = FilePreviewComponentMode.iFramePreview;
            }
        );
    }

    public applyNewSignatureInBottomRightCorner(): void {
        const container = $(`#${this.imageContainerId}`);

        const newElement = DraggableImageOverlayElement.Create(DraggableImageOverlayType.Signature, this.state.actualPage);
        if (container) {
            newElement.left = container.width() - newElement.width;
            newElement.top = container.height() - newElement.height;
            newElement.containerWidth = container.width();
            newElement.containerHeight = container.height();
        }

        this.mutateState(
            s => s.draggableOverlays = s.draggableOverlays.concat(newElement),
            () => {
                this.props.onOverlaysChange?.(
                    this.state.draggableOverlays,
                    this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                );
            }
        );
    }

    public onSignButton(): void {
        if (this.state.mode === FilePreviewComponentMode.iFramePreview) {
            this.switchToEditMode();
            return;
        }

        // Add a second signature
        this.mutateState(
            s => {
                s.isLoaded = true;
                s.draggableOverlays.push(DraggableImageOverlayElement.Create(DraggableImageOverlayType.Signature, this.state.actualPage));
                s.mode = FilePreviewComponentMode.imageEdit;
            },
            () => {
                this.props.onOverlaysChange?.(
                    this.state.draggableOverlays,
                    this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                );
            }
        );
    }

    public async switchToEditMode(): Promise<void> {
        // Generate the filePreview without printed overlays when in edit mode. The overlays will be controlled by the frontEnd
        const loadedFile = await this.props.file.getFetchUrlDtoAsync(false, false, true);
        this.mutateState(
            s => {
                s.isLoaded = false;
                s.mode = FilePreviewComponentMode.imageEdit;
            },
            () => {
                // force iFrame content to be set a tick after the iFrame is added
                // this avoids an issue where width:max-content doesn't detect the pdf size correctly
                window.setTimeout(() => {
                    this.mutateState(
                        s => {
                            s.fetchUrl = loadedFile.FetchUrl;
                            if (this.props.file.draggableImageOverlayElement.length === 0 && this.props.file.isDefaultPositionSignatureRequired) {
                                // No Existing signature and no previous position records
                                // the signature needs to be applied after the PDF is loaded so it appears at the correct position
                                // if applied before, it also takes a very long time to render for unknown reasons
                                if (s.isLoaded)
                                    this.applyNewSignatureInBottomRightCorner();
                                else
                                    this.onEditModePreviewLoaded = () => this.applyNewSignatureInBottomRightCorner();
                            }
                            else {
                                s.draggableOverlays = this.copyFilesDraggableOverlays(this.props.file);
                                if (s.isLoaded)
                                    this.updateAllOverlayElementsPositioningData(s.draggableOverlays);
                                else
                                    this.onEditModePreviewLoaded = () => this.updateAllOverlayElementsPositioningData(s.draggableOverlays);
                            }
                        },
                        () => {
                            this.props.onOverlaysChange?.(
                                this.state.draggableOverlays,
                                this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                            );
                        }
                    );
                });
            }
        );
    }

    public async removeSignature(): Promise<ExportableFile> {
        const faxOverlayElements = this.props.file.overlayElements.filter(p => p.overlayElementType !== OverlayElementType.PrepositionedSignature);
        const exportableFile = this.props.file.getCleanCopy();

        if (exportableFile.overlayElements.length > 0) {
            exportableFile.removeOverlayElements();
        }

        if (faxOverlayElements.length > 0)
            exportableFile.applyDistinctOverlayElements(faxOverlayElements, OverlayElementType.InfoFaxReceiverContact);

        const file = cacheStore.ClinicSettings.EnableConsultationExamRequestAttachments && !!this.props.attachedDocuments
            ? await ExportableFileHelper.getConcatenatedFetchUrlDtoAsync(exportableFile, [this.props.attachedDocuments])
            : exportableFile;
        const fetchUrl = (await file.getFetchUrlDtoAsync(false, false, false, true))?.FetchUrl || "";

        this.mutateState(
            s => {
                if (!IsNullOrWhiteSpace(fetchUrl)) {
                    s.fetchUrl = fetchUrl;
                    this.props.onUpdateFetchUrl?.(fetchUrl);
                    s.isLoaded = false;
                }
            },
            () => {
                this.applyDraggableOverlaysToFile(this.state.draggableOverlays);
                this.props.onFilePreviewLoaded();
            }
        );

        return exportableFile;
    }

    public addSignature(): void {
        this.mutateState(
            s => s.draggableOverlays.push(DraggableImageOverlayElement.Create(DraggableImageOverlayType.Signature, this.state.actualPage)),
            () => {
                this.props.onOverlaysChange?.(
                    this.state.draggableOverlays,
                    this.props.file?.overlayElements?.filter(overlay => overlay.overlayElementType === OverlayElementType.PrepositionedSignature) as PrepositionedSignatureOverlayElement[]
                );
            }
        );
    }

    private getActualPage(currentPage: number): number {
        if (!this.props.file?.PageNumbers?.length)
            return currentPage;

        const duplicatedPages = this.props.file.PageNumbers.any(p => p === 1) || !this.props.documentPages?.any()
            ? 0
            : this.state.documentTotalPages - this.props.documentPages.length;

        if (currentPage === 1)
            return Math.min(...this.props.file.PageNumbers) + duplicatedPages;

        const numberOfPages = currentPage <= this.props.file.PageNumbers.length ?
            this.props.file.PageNumbers[currentPage - 1] :
            Math.max(...this.props.file.PageNumbers);

        let missingPages = 0;
        for (let i = 1; i <= numberOfPages; i++) {
            if (!this.props.file.PageNumbers.some(x => x === i)) {
                missingPages++;
            }
        }

        return currentPage + missingPages + duplicatedPages;
    }

    private onChangePage(page: number): void {
        if (this.state.draggableOverlays)
            this.updateAllOverlayElementsPositioningData(this.state.draggableOverlays.filter(x => x.page === this.state.currentPage));

        this.mutateState(s => {
            s.currentPage = page;
            s.actualPage = this.getActualPage(page);
        });
    }

    public getCurrentOverlay(): DraggableImageOverlayElement[] {
        return this.state.draggableOverlays || [];
    }

    private copyFilesDraggableOverlays(exportableFile: ExportableFile | null): DraggableImageOverlayElement[] {
        if (exportableFile?.draggableImageOverlayElement?.any()) {
            const overlays = this.state.draggableOverlays.length > 0
                ? this.state.draggableOverlays
                : exportableFile.draggableImageOverlayElement;

            return overlays.map(overlay => this.adjustOverlayToCurrentScreen(overlay));
        }

        return exportableFile?.draggableImageOverlayElement?.map(p => new DraggableImageOverlayElement(p)) || [];
    }

    private adjustOverlayToCurrentScreen(overlay: DraggableImageOverlayElement): DraggableImageOverlayElement {
        if (!overlay)
            return;

        const height = this.getImageContainerWidthAndTop().height;
        const containerWidth = $(`#${this.imageDrawZoneId}`).width();

        let ratio: number = 1;
        if (this.isPortrait)
            ratio = height > 0 && overlay.containerHeight !== 0 ? height / overlay.containerHeight : ratio;
        else
            ratio = containerWidth > 0 && overlay.containerWidth !== 0 ? containerWidth / overlay.containerWidth : ratio;

        return new DraggableImageOverlayElement({
            left: overlay.left * ratio,
            top: overlay.top * ratio,
            containerWidth: overlay.containerWidth * ratio,
            containerHeight: overlay.containerHeight * ratio,
            width: overlay.width * ratio,
            height: overlay.height * ratio,
            page: overlay.page,
            draggableImageOverlayType: overlay.draggableImageOverlayType,
            defaultwidth: overlay.defaultwidth,
            overlayElementType: overlay.overlayElementType,
            id: overlay.id
        });
    }

    private getImageContainerWidthAndTop(): { width: number, height: number } {
        const container = this.containerRef.current;

        return container
            ? { width: container.clientWidth, height: container.clientHeight }
            : { width: 0, height: 0 };
    }
}

export { FilePreviewComponentEditable, FilePreviewComponentMode };
